import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { notifications } from "@mantine/notifications";
import type { UserNotificationDto, UserNotificationType } from "@shared/notifications";
import { USER_NOTIFY_SOCKET_EVENT } from "@shared/notifications";
import { useUserContext } from "@/context/UserContext";
import trpc, { getToken } from "@/shared/utils/trpc";
import { getPortalSocketPath, getPortalSocketUrl } from "@/shared/constants/portalSocket.const";
import { getActiveCollaborationChatMatchId } from "@/shared/utils/activeChatPresence";
import { NOTIFICATION_AUTO_CLOSE_MS } from "@/shared/constants/notification.const";

type NotificationContextValue = {
  items: UserNotificationDto[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const PENDING_COUNT_NOTIFICATION_TYPES = new Set<UserNotificationType>([
  "REQUEST_RECEIVED",
  "REQUEST_ACCEPTED",
  "REQUEST_DECLINED",
]);

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const {
    isLoggedIn,
    userDetails,
    refreshPendingInterestCount,
    refreshCollaborationSidebarCounts,
  } = useUserContext();
  const [items, setItems] = useState<UserNotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const refreshPendingInterestCountRef = useRef(refreshPendingInterestCount);
  const refreshCollaborationSidebarCountsRef = useRef(refreshCollaborationSidebarCounts);
  refreshPendingInterestCountRef.current = refreshPendingInterestCount;
  refreshCollaborationSidebarCountsRef.current = refreshCollaborationSidebarCounts;

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setItems([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        trpc.notification.list.query({ page: 1, limit: 30 }),
        trpc.notification.unreadCount.query(),
      ]);
      setItems(listRes.items as UserNotificationDto[]);
      setUnreadCount(countRes.count);
    } catch (err) {
      console.error("[notifications] refresh failed:", err);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  const markRead = useCallback(async (id: string) => {
    try {
      await trpc.notification.markRead.mutate({ id });
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: now } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error("[notifications] markRead failed:", err);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await trpc.notification.markAllRead.mutate();
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? now })));
      setUnreadCount(0);
    } catch (err) {
      console.error("[notifications] markAllRead failed:", err);
    }
  }, []);

  const handleIncoming = useCallback((dto: UserNotificationDto) => {
    const activeChatMatchId = getActiveCollaborationChatMatchId();
    const chatMatchId =
      dto.type === "CHAT_MESSAGE" && dto.metadata && typeof dto.metadata.matchId === "string"
        ? dto.metadata.matchId
        : null;
    if (dto.type === "CHAT_MESSAGE" && chatMatchId && chatMatchId === activeChatMatchId) {
      return;
    }

    if (!dto.readAt) {
      setUnreadCount((c) => c + 1);
    }
    setItems((prev) => {
      if (prev.some((n) => n.id === dto.id)) return prev;
      return [dto, ...prev].slice(0, 50);
    });
    if (document.visibilityState === "visible") {
      notifications.show({
        title: dto.title,
        message: dto.body,
        color: "blue",
        autoClose: NOTIFICATION_AUTO_CLOSE_MS,
      });
    }

    if (PENDING_COUNT_NOTIFICATION_TYPES.has(dto.type)) {
      refreshPendingInterestCountRef.current();
      refreshCollaborationSidebarCountsRef.current();
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !userDetails?.id) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setItems([]);
      setUnreadCount(0);
      return;
    }

    void refresh();

    const token = getToken();
    if (!token) return;

    const socketUrl = getPortalSocketUrl();
    if (!socketUrl) return;

    const socket = io(socketUrl, {
      path: getPortalSocketPath(),
      auth: { token },
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on(USER_NOTIFY_SOCKET_EVENT, (payload: UserNotificationDto) => {
      if (payload?.id) handleIncoming(payload);
    });

    return () => {
      socket.off(USER_NOTIFY_SOCKET_EVENT);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [isLoggedIn, userDetails?.id, refresh, handleIncoming]);

  const value: NotificationContextValue = {
    items,
    unreadCount,
    loading,
    refresh,
    markRead,
    markAllRead,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
